/**
 * The "Find a resident" door, mounted in the PA, UMED, Finance and Scheduling / Manifest Review shells.
 *
 * It asks the server once whether the door should exist (GET /resident-directory/access). Only
 * `enabled && can_search` draws anything; a refusal, a 404, a 503 or a network failure draws nothing at all,
 * not an error banner: a person who may not search should not see a button, or a message about one.
 *
 * The button opens a search panel; a result opens the read-only record in a drawer above it. Escape closes the
 * drawer first, then the panel. The panel and drawer are portalled to <body>, because the shells' bars carry
 * their own stacking contexts and a fixed overlay inside one would be clipped to it.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { residentDirectoryApi } from './residentDirectoryApi';
import ResidentDirectory from './ResidentDirectory';
import ResidentRecordDrawer from './ResidentRecordDrawer';
import './residentDirectory.css';

const FOCUSABLE = 'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Whether the door exists for this caller. False while asking and on any failure. */
export function useResidentDirectoryAccess(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    let live = true;
    residentDirectoryApi.access().then((a) => { if (live) setOpen(Boolean(a && a.enabled && a.can_search)); }).catch(() => undefined);
    return () => { live = false; };
  }, []);
  return open;
}

function SearchPanel({ onClose }: { onClose: () => void }) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  const titleId = useId();
  const [selected, setSelected] = useState<{ id: string; trigger: HTMLElement | null } | null>(null);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null;
    (panelRef.current?.querySelector<HTMLElement>('input') || panelRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      // The drawer, when open, owns Escape and Tab.
      if (selectedRef.current || e.defaultPrevented) return;
      if (e.key === 'Escape') { e.preventDefault(); closeRef.current(); return; }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const items = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (items.length === 0) { e.preventDefault(); panelRef.current.focus(); return; }
      const first = items[0];
      const last = items[items.length - 1];
      const at = document.activeElement;
      if (e.shiftKey && (at === first || at === panelRef.current)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && at === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      before?.focus?.();
    };
  }, []);

  return createPortal(
    <div className="rd-root">
      <div className="rd-backdrop" onClick={() => { if (!selectedRef.current) closeRef.current(); }} aria-hidden="true" />
      <div className="rd-panel" role="dialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1} ref={panelRef}>
        <div className="rd-drawer-top">
          <h2 id={titleId} className="rd-title">Find a resident</h2>
          <button type="button" className="rd-ghost" onClick={onClose}>Close</button>
        </div>
        <p className="rd-meta-line">Search is logged. It shows who owns a resident and where they stand; it does not change anything.</p>
        <ResidentDirectory onOpen={(id, trigger) => setSelected({ id, trigger })} />
      </div>
      {selected && (
        <ResidentRecordDrawer
          patientId={selected.id}
          returnFocusTo={selected.trigger}
          onClose={() => setSelected(null)}
          onNavigate={onClose}
        />
      )}
    </div>,
    document.body,
  );
}

export default function FindResidentEntry({ className }: { className?: string }) {
  const allowed = useResidentDirectoryAccess();
  const [open, setOpen] = useState(false);
  if (!allowed) return null;
  return (
    <>
      <button type="button" className={`rd-trigger${className ? ` ${className}` : ''}`} aria-haspopup="dialog" onClick={() => setOpen(true)}>
        Find a resident
      </button>
      {open && <SearchPanel onClose={() => setOpen(false)} />}
    </>
  );
}
